/* Candidate helper. No automatic listeners, tracking calls or Meta integration. */
(function (root) {
  'use strict';
  const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
  const code = /^[A-F0-9]{24}$/;
  const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const clickFields = ['gclid', 'gbraid', 'wbraid'];
  const clickPattern = /^[A-Za-z0-9_-]{1,512}$/;
  function originFrom(search, catalog) {
    const params = new URLSearchParams(search);
    const origin = {};
    for (const field of fields) {
      const value = params.get(field);
      if (value && Array.isArray(catalog[field]) && catalog[field].includes(value)) origin[field] = value;
    }
    // Click IDs are opaque and case-sensitive; never normalize or copy the full URL.
    for (const field of clickFields) {
      const values = params.getAll(field);
      if (values.length === 1 && clickPattern.test(values[0])) origin[field] = values[0];
    }
    return origin;
  }
  function create(options) {
    const { enabled = false, endpoint, allowedEndpoint, fetchImpl, storage, randomUUID, catalog = {}, timeoutMs = 5000, consentGranted = () => false } = options;
    // Endpoint supplied from reviewed deployment config. No dynamic destination from a URL parameter.
    if (!enabled) return { register: async () => ({ status: 'disabled' }), capture: () => {}, decorateMessage: async message => ({ message, tracking: 'disabled' }) };
    if (!endpoint || endpoint !== allowedEndpoint || new URL(endpoint).protocol !== 'https:') throw new Error('UNAPPROVED_ENDPOINT');
    const inflight = new Map();
    function capture(search) {
      if (consentGranted() !== true) return;
      const origin = originFrom(search, catalog);
      // First known campaign survives storefront navigation; absence stays unknown.
      if (Object.keys(origin).length && storage.getItem('natu_attr_first_origin') === null) storage.setItem('natu_attr_first_origin', JSON.stringify(origin));
      // Keep the first Google touch independently: an earlier organic UTM must not erase a paid click.
      if (clickFields.some(k => Object.hasOwn(origin, k)) && storage.getItem('natu_attr_google_origin') === null) storage.setItem('natu_attr_google_origin', JSON.stringify(origin));
    }
    async function register(requestKey, search) {
      if (consentGranted() !== true) return { status: 'consent_required' };
      if (!uuid.test(requestKey)) throw new Error('INVALID_REQUEST_KEY');
      let journey = storage.getItem('natu_attr_journey');
      if (!journey || !uuid.test(journey)) { journey = randomUUID(); if (!uuid.test(journey)) throw new Error('INVALID_JOURNEY'); storage.setItem('natu_attr_journey', journey); }
      const slot = 'natu_attr_request_' + requestKey;
      // Freeze original attribution across retry/navigation. Contains no cart/customer data.
      let payload = storage.getItem(slot);
      if (payload === null) {
        capture(search);
        const first = storage.getItem('natu_attr_google_origin') ?? storage.getItem('natu_attr_first_origin');
        payload = JSON.stringify({ journey_id: journey, request_key: requestKey, origin: first === null ? {} : JSON.parse(first) });
        storage.setItem(slot, payload);
      }
      const saved = JSON.parse(payload);
      if (!uuid.test(saved.journey_id) || saved.request_key !== requestKey || !saved.origin || typeof saved.origin !== 'object' || Array.isArray(saved.origin) || Object.keys(saved).some(k => !['journey_id','request_key','origin'].includes(k)) || Object.keys(saved.origin).some(k => clickFields.includes(k) ? !clickPattern.test(saved.origin[k]) || typeof saved.origin[k] !== 'string' : !fields.includes(k) || !Array.isArray(catalog[k]) || !catalog[k].includes(saved.origin[k]))) throw new Error('INVALID_SAVED_REQUEST');
      if (inflight.has(requestKey)) return inflight.get(requestKey);
      const run = (async () => {
        const controller = new AbortController();
        let timer;
        try {
          const work = (async () => {
            const response = await fetchImpl(endpoint, { method: 'POST', credentials: 'omit', redirect: 'error', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: payload });
            if (!response.ok) throw new Error('ATTRIBUTION_UNCONFIRMED');
            const result = await response.json();
            if (!uuid.test(result.lead_id) || !code.test(result.commercial_code) || result.event !== 'whatsapp_requested') throw new Error('INVALID_REGISTRATION_RESPONSE');
            return { status: 'registered', lead_id: result.lead_id, commercial_code: result.commercial_code, messageSuffix: '\nCódigo da solicitação: ' + result.commercial_code };
          })();
          const timeout = new Promise((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('ATTRIBUTION_TIMEOUT')); }, Math.max(500, Math.min(10000, timeoutMs))); });
          return await Promise.race([work, timeout]);
        } finally { clearTimeout(timer); }
      })();
      inflight.set(requestKey, run);
      try { return await run; } finally { inflight.delete(requestKey); }
    }
    async function decorateMessage(message, requestKey, search) {
      if (typeof message !== 'string') throw new Error('MESSAGE_REQUIRED');
      try {
        const result = await register(requestKey, search);
        return { message: result.status === 'registered' ? message + result.messageSuffix : message, tracking: result.status };
      } catch {
        // Cart items/notes remain byte-for-byte intact; caller offers the unchanged WhatsApp message.
        return { message, tracking: 'unconfirmed' };
      }
    }
    return { register, capture, decorateMessage };
  }
  const api = { create, originFrom };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.NatuAttributionCandidate = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
